import type { StoredUsers } from '~/gql/users/users'
import type { UserAction } from '~/pages/backoffice/admin/user/list/user-actions'
import { MoreHorizontal } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { tourAnchor } from '~/helpers/tour/anchors'
import { userDisplayName } from '~/helpers/users/display-name'

const ACTION_LABEL_KEYS: Record<UserAction, string> = {
  sessions: 'user.sessions.view',
  edit: 'common.update',
  delete: 'common.delete',
  impersonate: 'common.impersonate',
  resendInvitation: 'resend.email',
  resetCredentials: 'credentials.reset.action',
}

type IProps = {
  user: StoredUsers
  /** Les actions ouvertes sur ce compte, cf. `availableUserActions`. Vide : pas de menu. */
  actions: readonly UserAction[]
  onAction: (action: UserAction, user: StoredUsers) => void
}

/** Le menu d'actions d'une ligne de la liste des comptes. */
function UserRowActions({ user, actions, onAction }: IProps) {
  const { t } = useTranslation()
  if (actions.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="size-8 p-0" {...tourAnchor('users.actions')}>
          <span className="sr-only">{t('common.actionFor', { action: t('common.action'), name: userDisplayName(user) })}</span>
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map(action => (
          <DropdownMenuItem
            key={action}
            className={action === 'delete' ? 'text-destructive focus:text-destructive' : undefined}
            onClick={() => onAction(action, user)}
          >
            {t(ACTION_LABEL_KEYS[action])}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default UserRowActions
